import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ClientSyncService, ClientSyncConfig, BulkSyncPayload, BulkSyncTable, BulkSyncColumn,
  DataSetOption, DataModelTable, FieldModelDto, SyncConnection, AGGREGATION_TYPES
} from '../../../services/client-sync-service';
import { DialogService } from '../../utility/dialog/dialog-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';

interface BulkSyncTableUI {
  tableKey: string;
  Schema: string;
  TableName: string;
  GroupByColumns: string[];
  Columns: BulkSyncColumn[];
  pendingGroupByColumn: string;
  tableSearch: string;
  filteredTableOptions: DataModelTable[];
  expanded: boolean;
  tableDropdownOpen: boolean;
  dropdownTop: number;
  dropdownLeft: number;
  dropdownWidth: number;
  tableFields: FieldModelDto[];
  loadingFields: boolean;
  // TableModel metadata
  tableId: string;
  displayName: string;
  fromMajorVersion: number;
  fromMinorVersion: number;
  savingTableInfo: boolean;
}

interface BulkSyncPayloadUI {
  AllFields: boolean;
  LegacySync: boolean;
  NumberThreads: number;
  ModDateField: string;
  ClientConnectionId: string;
  Tables: BulkSyncTableUI[];
}

type EditableSyncConfig = ClientSyncConfig & {
  editing?: boolean;
  payloadUI?: BulkSyncPayloadUI;
  viewExpanded?: boolean;
};

@Component({
  selector: 'app-client-sync-editor',
  templateUrl: './client-sync-editor.component.html',
  styleUrls: ['./client-sync-editor.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ClientSyncEditorComponent implements OnInit, OnChanges {
  @Input() clientId: string | null = null;

  configs: EditableSyncConfig[] = [];
  dataSets: DataSetOption[] = [];
  connections: SyncConnection[] = [];
  private allTables: DataModelTable[] = [];
  private modelLoaded = false;
  private originalCopies: Record<string, string> = {};

  loading = false;
  error: string | null = null;

  readonly aggTypes = AGGREGATION_TYPES;

  constructor(private syncService: ClientSyncService, private dialog: DialogService) {}

  ngOnInit(): void {
    if (this.clientId) this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) this.init();
  }

  private init(): void {
    this.loadDataSets();
    this.loadConnections();
    this.loadConfigs();
    this.loadAllTables();
  }

  // -------- Load --------

  loadConfigs(): void {
    if (!this.clientId) return;
    this.loading = true;
    this.syncService.getConfigs(this.clientId, configs => {
      this.configs = configs.map(c => ({ ...c, editing: false }));
      this.loading = false;
    });
  }

  private loadDataSets(): void {
    if (!this.clientId) return;
    this.syncService.getDataSets(this.clientId, sets => { this.dataSets = sets; });
  }

  private loadConnections(): void {
    if (!this.clientId) return;
    this.syncService.getConnections(this.clientId, conns => { this.connections = conns; });
  }

  private loadAllTables(): void {
    if (this.modelLoaded) return;
    this.syncService.getDataModel('', tables => {
      console.log('loadAllTables response:', tables);
      this.allTables = tables;
      this.modelLoaded = true;
    });
  }

  private ensureDataModel(then: () => void): void {
    if (this.modelLoaded) { then(); return; }
    this.syncService.getDataModel('', tables => {
      this.allTables = tables;
      this.modelLoaded = true;
      then();
    });
  }

  // -------- Template helpers --------

  getDataSetName(dataSetId: string): string {
    return this.dataSets.find(ds => ds.id === dataSetId)?.name ?? '';
  }

  getFilteredTables(search: string): DataModelTable[] {
    if (!search?.trim()) return this.allTables;
    const q = search.toLowerCase();
    return this.allTables.filter(t =>
      t.tableName.toLowerCase().includes(q) || (t.schema ?? '').toLowerCase().includes(q)
    );
  }

  getColumnsForTableKey(tableKey: string): string[] {
    const [schema, ...rest] = tableKey.split('||');
    const tableName = rest.join('||');
    return this.allTables.find(
      t => t.schema === schema && t.tableName === tableName
    )?.columns ?? [];
  }

  getColumnOptionsForTable(table: BulkSyncTableUI): string[] {
    // Priority 1: full field list loaded from _Models
    if (table.tableFields.length > 0) {
      return table.tableFields.map(f => f.fieldName);
    }
    // Priority 2: columns from the allTables cache
    const fromCache = this.getColumnsForTableKey(table.tableKey);
    if (fromCache.length > 0) return fromCache;
    // Priority 3: derive from what's already configured so old records stay editable
    const existing = new Set<string>();
    table.GroupByColumns.forEach(c => existing.add(c));
    table.Columns.forEach(c => { if (c.ColumnName) existing.add(c.ColumnName); });
    return Array.from(existing).sort();
  }

  // -------- Config lifecycle --------

  addConfig(): void {
    if (!this.clientId) return;
    if (this.configs.some(c => !c.id && c.editing)) return;

    const newConfig: EditableSyncConfig = {
      id: '',
      name: '',
      clientId: this.clientId,
      dataSetId: '',
      enabled: true,
      jobKey: 'Bulk Import',
      jobPayload: '',
      editing: true,
      payloadUI: this.defaultPayload()
    };
    this.configs.unshift(newConfig);
  }

  editConfig(config: EditableSyncConfig): void {
    this.configs.forEach(c => { if (c !== config && c.editing) this.cancelEdit(c); });
    this.originalCopies[config.id || 'new'] = JSON.stringify({ ...config, editing: false, payloadUI: undefined });

    const openEdit = () => {
      config.payloadUI = this.parsePayload(config.jobPayload);
      config.payloadUI.Tables.forEach(table => {
        if (!table.Schema && !table.TableName) return;

        // Reconcile casing + pull metadata from the data model when the table is found.
        if (this.allTables.length) {
          const match =
            this.allTables.find(m =>
              (m.schema ?? '').toLowerCase() === (table.Schema ?? '').toLowerCase() &&
              m.tableName.toLowerCase() === table.TableName.toLowerCase()
            ) ??
            this.allTables.find(m =>
              m.tableName.toLowerCase() === table.TableName.toLowerCase()
            );
          if (match) {
            table.Schema    = match.schema ?? '';
            table.TableName = match.tableName;
            table.tableKey  = `${match.schema ?? ''}||${match.tableName}`;
            table.tableId         = match.id;
            table.displayName     = match.displayName;
            table.fromMajorVersion = match.fromMajorVersion;
            table.fromMinorVersion = match.fromMinorVersion;
            if (match.columns.length) {
              table.Columns = table.Columns.map(col => {
                const mc = match.columns.find(c => c.toLowerCase() === col.ColumnName.toLowerCase());
                return mc ? { ...col, ColumnName: mc } : col;
              });
              table.GroupByColumns = table.GroupByColumns.map(gb =>
                match.columns.find(c => c.toLowerCase() === gb.toLowerCase()) ?? gb
              );
            }
          }
        }

        // Load fields using the tableId — either resolved above or stored in the JSON.
        if (table.tableId) {
          table.loadingFields = true;
          this.syncService.getFieldsByTableId(table.tableId, fields => {
            table.tableFields = fields;
            table.loadingFields = false;
          });
        }
      });
      config.editing = true;
    };

    this.ensureDataModel(openEdit);
  }

  saveConfig(config: EditableSyncConfig): void {
    if (!config.name?.trim()) { this.error = 'Name is required.'; return; }
    this.error = null;

    if (!config.id) config.id = crypto.randomUUID();

    const toSave: ClientSyncConfig = {
      id: config.id,
      name: config.name.trim(),
      clientId: this.clientId!,
      dataSetId: config.dataSetId,
      enabled: config.enabled,
      jobKey: 'Bulk Import',
      jobPayload: this.serializePayload(config.payloadUI!)
    };

    this.syncService.saveConfig(toSave, () => {
      config.editing = false;
      config.jobPayload = toSave.jobPayload;
      this.loadConfigs();
    }, () => {
      this.error = 'Failed to save configuration.';
    });
  }

  cancelEdit(config: EditableSyncConfig): void {
    const key = config.id || 'new';
    if (!config.id) {
      this.configs = this.configs.filter(c => c !== config);
      return;
    }
    const snapshot = this.originalCopies[key];
    if (snapshot) {
      Object.assign(config, JSON.parse(snapshot));
      delete this.originalCopies[key];
    }
    config.editing = false;
    config.payloadUI = undefined;
  }

  deleteConfig(config: EditableSyncConfig): void {
    this.dialog.openModal(
      'Confirm Deletion',
      `Delete sync config "${config.name}"?`,
      ModalType.QUESTION,
      () => {
        this.syncService.deleteConfig(config.id, () => {
          this.configs = this.configs.filter(c => c !== config);
        }, () => {
          this.error = 'Failed to delete configuration.';
        });
      }
    );
  }

  // -------- Tables --------

  addTable(config: EditableSyncConfig): void {
    config.payloadUI?.Tables.push({
      tableKey: '', Schema: '', TableName: '',
      GroupByColumns: [], Columns: [], pendingGroupByColumn: '', tableSearch: '',
      expanded: true,
      filteredTableOptions: [...this.allTables], tableDropdownOpen: false,
      dropdownTop: 0, dropdownLeft: 0, dropdownWidth: 0,
      tableFields: [], loadingFields: false,
      tableId: '', displayName: '', fromMajorVersion: 0, fromMinorVersion: 0, savingTableInfo: false
    });
  }

  removeTable(config: EditableSyncConfig, index: number): void {
    config.payloadUI?.Tables.splice(index, 1);
  }

  openDropdown(table: BulkSyncTableUI, event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    table.dropdownTop = rect.bottom + 2;
    table.dropdownLeft = rect.left;
    table.dropdownWidth = rect.width;
    table.tableDropdownOpen = !table.tableDropdownOpen;
  }

  filterTableOptions(table: BulkSyncTableUI, search: string): void {
    if (!search?.trim()) {
      table.filteredTableOptions = [...this.allTables];
      return;
    }
    const q = search.toLowerCase();
    table.filteredTableOptions = this.allTables.filter(t =>
      t.tableName.toLowerCase().includes(q) || (t.schema ?? '').toLowerCase().includes(q)
    );
  }

  onTableKeyChange(config: EditableSyncConfig, table: BulkSyncTableUI, key: string): void {
    table.tableKey = key ?? '';
    const [schema, ...rest] = (key ?? '').split('||');
    table.Schema = schema ?? '';
    table.TableName = rest.join('||') ?? '';
    table.GroupByColumns = [];
    table.Columns = [];
    table.pendingGroupByColumn = '';
    table.tableSearch = '';
    table.filteredTableOptions = [...this.allTables];
    table.tableDropdownOpen = false;

    const match = this.allTables.find(t => `${t.schema ?? ''}||${t.tableName}` === key);
    if (match) {
      table.tableId = match.id;
      table.displayName = match.displayName;
      table.fromMajorVersion = match.fromMajorVersion;
      table.fromMinorVersion = match.fromMinorVersion;
      table.tableFields = [];
      table.loadingFields = true;
      this.syncService.getFieldsByTableId(match.id, fields => {
        table.tableFields = fields;
        table.loadingFields = false;
      });
    } else {
      table.tableId = '';
      table.displayName = '';
      table.fromMajorVersion = 0;
      table.fromMinorVersion = 0;
      table.tableFields = [];
      table.loadingFields = false;
    }
  }

  selectTable(config: EditableSyncConfig, table: BulkSyncTableUI, t: DataModelTable): void {
    this.onTableKeyChange(config, table, `${t.schema ?? ''}||${t.tableName}`);
  }

  saveTableModel(table: BulkSyncTableUI): void {
    if (!table.tableId) return;
    table.savingTableInfo = true;
    this.syncService.updateTableModel(
      table.tableId, table.displayName, table.fromMajorVersion, table.fromMinorVersion,
      () => {
        table.savingTableInfo = false;
        const cached = this.allTables.find(t => t.id === table.tableId);
        if (cached) {
          cached.displayName = table.displayName;
          cached.fromMajorVersion = table.fromMajorVersion;
          cached.fromMinorVersion = table.fromMinorVersion;
        }
      },
      () => { table.savingTableInfo = false; }
    );
  }

  // -------- Group By --------

  addGroupBy(table: BulkSyncTableUI): void {
    const col = table.pendingGroupByColumn;
    if (col && !table.GroupByColumns.includes(col)) {
      table.GroupByColumns.push(col);
    }
    table.pendingGroupByColumn = '';
  }

  removeGroupBy(table: BulkSyncTableUI, index: number): void {
    table.GroupByColumns.splice(index, 1);
  }

  // -------- Columns --------

  addColumn(table: BulkSyncTableUI): void {
    table.Columns.push({ AggregationType: 'None', ColumnName: '', ColumnMapping: null });
  }

  removeColumn(table: BulkSyncTableUI, index: number): void {
    table.Columns.splice(index, 1);
  }

  // -------- Payload helpers --------

  private defaultPayload(): BulkSyncPayloadUI {
    return {
      AllFields: false,
      LegacySync: false,
      NumberThreads: 10,
      ModDateField: '',
      ClientConnectionId: '',
      Tables: []
    };
  }

  private parsePayload(raw: string): BulkSyncPayloadUI {
    const ui = this.defaultPayload();
    if (!raw) return ui;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const p: any = JSON.parse(raw);
      ui.AllFields     = p.AllFields     ?? p.allFields     ?? false;
      ui.LegacySync    = p.LegacySync    ?? p.legacySync    ?? false;
      ui.NumberThreads = p.NumberThreads ?? p.numberThreads ?? 10;
      ui.ModDateField  = p.ModDateField  ?? p.modDateField  ?? '';
      ui.ClientConnectionId = p.ClientConnectionId ?? p.clientConnectionId ?? '';
      const tables: any[] = p.Tables ?? p.tables ?? [];
      ui.Tables = tables.map((t: any) => {
        const schema    = t.Schema    ?? t.schema    ?? '';
        const tableName = t.TableName ?? t.tableName ?? '';
        const groupBy: string[]     = t.GroupByColumns ?? t.groupByColumns ?? [];
        const cols: any[]           = t.Columns        ?? t.columns        ?? [];
        return {
          tableKey: schema && tableName ? `${schema}||${tableName}` : '',
          Schema: schema,
          TableName: tableName,
          GroupByColumns: groupBy,
          Columns: cols.map((c: any) => ({
            AggregationType: c.AggregationType ?? c.aggregationType ?? 'None',
            ColumnName:      c.ColumnName      ?? c.columnName      ?? '',
            ColumnMapping:   c.ColumnMapping   ?? c.columnMapping   ?? null
          })),
          pendingGroupByColumn: '',
          tableSearch: '',
          expanded: false,
          filteredTableOptions: [...this.allTables], tableDropdownOpen: false,
          dropdownTop: 0, dropdownLeft: 0, dropdownWidth: 0,
          tableFields: [], loadingFields: false,
          tableId: t.TableId ?? t.tableId ?? '',
          displayName: '', fromMajorVersion: 0, fromMinorVersion: 0, savingTableInfo: false
        };
      });
    } catch {
      // Malformed payload — fall back to defaults.
    }
    return ui;
  }

  private serializePayload(ui: BulkSyncPayloadUI): string {
    const payload: BulkSyncPayload = {
      AllFields: ui.AllFields,
      LegacySync: ui.LegacySync,
      NumberThreads: ui.NumberThreads,
      TablePrefix: null,
      ModDateField: ui.ModDateField,
      ClientConnectionId: ui.ClientConnectionId,
      Tables: ui.Tables.map(t => ({
        TableId: t.tableId,
        Schema: t.Schema,
        TableName: t.TableName,
        GroupByColumns: t.GroupByColumns,
        Columns: t.Columns.map(c => ({
          AggregationType: c.AggregationType,
          ColumnName: c.ColumnName,
          ColumnMapping: c.ColumnMapping?.trim() || null
        }))
      }))
    };
    return JSON.stringify(payload);
  }

  // -------- View mode helpers --------

  getPayloadTables(raw: string): BulkSyncTable[] {
    if (!raw) return [];
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const p: any = JSON.parse(raw);
      const tables: any[] = p.Tables ?? p.tables ?? [];
      return tables.map((t: any) => ({
        Schema:         t.Schema    ?? t.schema    ?? '',
        TableName:      t.TableName ?? t.tableName ?? '',
        GroupByColumns: t.GroupByColumns ?? t.groupByColumns ?? [],
        Columns: (t.Columns ?? t.columns ?? []).map((c: any) => ({
          AggregationType: c.AggregationType ?? c.aggregationType ?? 'None',
          ColumnName:      c.ColumnName      ?? c.columnName      ?? '',
          ColumnMapping:   c.ColumnMapping   ?? c.columnMapping   ?? null
        }))
      }));
    } catch {
      return [];
    }
  }

  columnPreview(columns: BulkSyncColumn[]): string {
    return columns.slice(0, 6).map(c => c.ColumnName).join(', ');
  }

  // -------- JSON import --------

  triggerImport(): void {
    (document.getElementById('syncImportFile') as HTMLInputElement)?.click();
  }

  onFileImport(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);

        // Accept either a full ClientSyncConfig JSON or a raw BulkSyncPayload.
        const isFullConfig = ('name' in json || 'Name' in json) && ('jobPayload' in json || 'JobPayload' in json);
        const rawPayload = isFullConfig
          ? (json.jobPayload ?? json.JobPayload ?? '')
          : JSON.stringify(json); // treat the whole file as a BulkSyncPayload

        const config: EditableSyncConfig = {
          id: '',
          name: json.name ?? json.Name ?? '',
          clientId: this.clientId!,
          dataSetId: json.dataSetId ?? json.DataSetId ?? '',
          enabled: json.enabled ?? json.Enabled ?? true,
          jobKey: 'Bulk Import',
          jobPayload: rawPayload,
          editing: true,
          payloadUI: this.parsePayload(rawPayload)
        };

        this.configs.unshift(config);
      } catch {
        this.error = 'Failed to parse JSON file. Ensure it is valid JSON.';
      }
      (event.target as HTMLInputElement).value = '';
    };
    reader.readAsText(file);
  }

  trackByIndex(index: number): number {
    return index;
  }
}
